import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';
import { delay, finalize, takeUntil, tap } from 'rxjs/operators';

import {
  BulkDatabaseVersion,
  BulkRecipientRequest,
  CreateScheduledMessageBulkCommand,
  CustomResultOfGetWhiteMessageCalculationsDto,
  GenderType,
  GetScheduledMessageCalculationsByBulkQuery,
  GetWhiteMessageCalculationsDto,
  SendTypes,
  SimType,
  SmsAggregator,
  SocialMediaSendDetailModel,
  VoiceMessageDetailsRequest,
} from 'src/app/core/api/models';
import { ScheduledMessagesService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { SendDateTimeType } from 'src/app/core/enums/send-date-time-type.enum';
import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { DynamicTabService } from 'src/app/core/services/dynamic-tab.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { TabsDynamicComponent } from 'src/app/layout/components/tabs-dynamic/tabs-dynamic.component';
import { MapPostalCodeSendSmsInfo } from 'src/app/shared/components/postal-code-map-selector/postal-code-map-selector.component';
import { BaseFormPageComponent } from '../../shared/base/components/base-form-page.component';
import { CampaignsFormComponent } from '../campaigns/campaigns-form/campaigns-form.component';
import { SendSmsConfirmationComponent } from '../send-sms-confirmation/send-sms-confirmation.component';
import { AppStateService } from './../../../core/services/app-state.service';
import {
  BaseSendSms,
  BaseSendSmsConfig,
} from './../base/base-send-sms/base-send-sms.component';

@Component({
  selector: 'app-send-sms-from-map',
  templateUrl: './send-sms-from-map.component.html',
  styleUrls: [
    './send-sms-from-map.component.scss',
    '../base/base-sending-system.component.scss',
  ],
  standalone: false,
})
export class SendSmsFromMapComponent
  extends BaseFormPageComponent
  implements OnInit, OnDestroy
{
  appStateService = inject(AppStateService);
  //#region properties
  formId: string = 'SendSmsFromMapForm';
  tabName: string = 'sendSMSFromMap';
  readonly mapStep = viewChild<MatStep>('mapStep');
  readonly stepper = viewChild<MatStepper>('stepper');

  title: string = '';
  model: CreateScheduledMessageBulkCommand = {};
  baseSendSmsConfig: BaseSendSmsConfig;
  smsCount: number;
  privateNumber: string;
  postalCodes: MapPostalCodeSendSmsInfo[] = null;
  isVisibleHelpPage: boolean = false;
  get bulkDatabaseVersion() {
    return BulkDatabaseVersion;
  }

  calcLoading: boolean;
  sendLoading: boolean;

  //#endregion

  constructor(
    public dynamicTabService: DynamicTabService,
    protected translate: TranslateService,
    public dialogService: DialogService,
    public utilityService: UtilityService,
    protected currentService: ScheduledMessagesService,
    protected tabsDynamicComponent: TabsDynamicComponent
  ) {
    super();
    this.baseCreateGetApiForSend(
      this.currentService,
      'scheduledMessagesCreateGet'
    );
  }

  ngOnInit(): void {
    try {
      this.pageVocabs = 'pages.sendSmsFromMap.';
      this.fields = 'pages.sendSmsFromMap.fields.';
      super.ngOnInit();
      this.privateNumber = this.formPageConfig?.editData?.senderNumber;
      this.configBaseSendSMS();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  closeThisTab() {
    try {
      const TimeOut = setTimeout(() => {
        this.tabsDynamicComponent.manualCloseTab(this.tabName);
        clearTimeout(TimeOut);
      }, 50);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#region init

  initForm() {
    this.form = new FormGroup({});
  }

  initStep2(): void {}

  configBaseSendSMS() {
    try {
      this.title = 'pages.sendSmsFromMap.title';
      this.baseSendSmsConfig = {
        title: 'pages.sendSmsFromMap.title',
        isEdit: false,
        msgInfoClassList: 'md:tw-w-1/3 tw-mt-2',
        sendDateTimeClassList: 'md:tw-w-1/3 tw-mt-2',
        isShowReceivers: false,
        isShowExtraReceivers: false,
        isShowFastSend: false,
        isAddSignature: !this.formPageConfig?.isEditMode ? true : false,
        isAddHeader: !this.formPageConfig?.isEditMode ? true : false,
        isShowPageHeader: false,
        sendType: SendTypes.Bulk,
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#endregion

  //#region api
  baseCreateApi(model: any) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .scheduledMessagesCreatePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe((result) => {
        this.apiNotificationService.notifyMessage({
          result,
          mode: 'matDialog',
        });
        if (result.isSucceed) this.onSuccess();
      });
  }

  //#endregion

  onMapConfirmed(e: MapPostalCodeSendSmsInfo[]) {
    try {
      if (e.length <= 0) {
        this.dialogService.openDialog({
          message: this.translate.instant(
            this.pageVocabs + 'errors.mostSelectSomeRegionOnMap'
          ),
          title: this.translate.instant('common.error'),
          status: DialogStatus.error,
        });
        return;
      }
      this.postalCodes = e;
      this.mapStep().completed = true;
      this.initStep2();
      this.stepper().next();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  getBaseSendSmsModel(baseModel: BaseSendSms) {
    try {
      this.smsCount = baseModel.smsCount;
      this.model.smsBody = baseModel.finalSmsBody;
      this.model.senderNumberId = baseModel.senderNumberId;
      this.model.isSendWhiteList = baseModel.isSendWhiteList;
      this.model.isFlashSms = baseModel.isFlashSms;
      this.model.description = baseModel.description;

      this.handleVoiceMessage(
        baseModel.isCanSendVoice,
        baseModel.voiceMessageDetails
      );

      this.handleSocialMediaMessage(
        baseModel.isCanSendSocialMedia,
        baseModel.socialMediaMessageDetails
      );

      //direct
      this.model.sendDateTimeChecked =
        baseModel.sendDateTimeType == SendDateTimeType.DateTime ||
        baseModel.sendDateTimeType == undefined
          ? true
          : false;
      this.model.sendDateTime = baseModel.directSendDateTime;
      // period
      this.model.sendPeriodChecked =
        baseModel.sendDateTimeType == SendDateTimeType.Period ? true : false;
      this.model.periodStart = baseModel.periodicSendStartDateTime;
      this.model.periodEnd = baseModel.periodicSendEndDateTime;
      this.model.periodText = baseModel.gradualPeriodText;
      this.model.periodTypeValue = baseModel.periodTypeValue;
      this.model.isSendWhiteList = baseModel.isSendWhiteList;
      this.model.isFlashSms = baseModel.isFlashSms;
      // gradual
      this.model.gradualChecked =
        baseModel.sendDateTimeType == SendDateTimeType.Gradual ? true : false;
      this.model.gradualStart = baseModel.gradualSendDateTime;
      this.model.gradualPageSize = baseModel.gradualPageSize;
      this.model.gradualPeriodText = baseModel.gradualPeriodText;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  handleVoiceMessage(
    isCanSendVoice,
    voiceMessageDetails: VoiceMessageDetailsRequest
  ) {
    try {
      if (isCanSendVoice) {
        if (voiceMessageDetails)
          this.model.voiceMessageDetails = voiceMessageDetails;

        this.model.campaignId = null;
        this.model.title = null;
        this.baseSendSmsConfig.isShowCampaign = false;
        this.baseSendSmsConfig = Object.assign({}, this.baseSendSmsConfig);
      } else {
        this.baseSendSmsConfig.isShowCampaign = true;
        this.baseSendSmsConfig = Object.assign({}, this.baseSendSmsConfig);
        this.model.voiceMessageDetails = null;
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  handleSocialMediaMessage(
    isCanSendSocialMedia,
    socialMediaMessageDetails: SocialMediaSendDetailModel
  ) {
    try {
      if (isCanSendSocialMedia) {
        if (socialMediaMessageDetails)
          this.model.socialMediaDetails = socialMediaMessageDetails;

        this.model.campaignId = null;
        this.model.title = null;
        this.baseSendSmsConfig.isShowCampaign = false;
        this.baseSendSmsConfig = Object.assign({}, this.baseSendSmsConfig);
      } else {
        this.baseSendSmsConfig.isShowCampaign = true;
        this.baseSendSmsConfig = Object.assign({}, this.baseSendSmsConfig);
        this.model.socialMediaDetails = null;
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  setBulkRecipients() {
    let requestModel: CreateScheduledMessageBulkCommand = {};
    requestModel = this.model;
    requestModel.bulkRecipients = [];
    if (this.postalCodes != null && this.postalCodes.length > 0) {
      this.postalCodes.forEach((item) => {
        let serverQuery: BulkRecipientRequest = {};
        //generate hamrah aval
        if (item.hamrahAvalInfo != null) {
          serverQuery = {
            stateId: item.stateId,
            cityId: item.cityId,
            aggregatorType: SmsAggregator.MCI,
            zipCode: Number.parseInt(item.postalCode),
            simType: SimType.All,
            fromIndex: item.hamrahAvalInfo.startFrom,
            totalCount: item.hamrahAvalInfo.totalCount,
            count: item.hamrahAvalInfo.sendCount,
            genderTye: GenderType.All,
            description: item.hamrahAvalInfo.description,
          };
          requestModel.bulkRecipients.push(serverQuery);
        }
        //generate irancell
        if (item.irancellInfo != null) {
          serverQuery = {
            stateId: item.stateId,
            cityId: item.cityId,
            aggregatorType: SmsAggregator.Irancell,
            zipCode: Number.parseInt(item.postalCode),
            simType: SimType.All,
            fromIndex: item.irancellInfo.startFrom,
            totalCount: item.irancellInfo.totalCount,
            count: item.irancellInfo.sendCount,
            genderTye: GenderType.All,
            description: item.irancellInfo.description,
          };
          requestModel.bulkRecipients.push(serverQuery);
        }
      });
    }
    this.model = requestModel;
  }

  onSubmit() {
    try {
      if (!this.isValidForm(this.form, this.tagName)) return;
      if (this.loading) return;

      this.setBulkRecipients();
      if (
        this.model.bulkRecipients.length >
        this.currentUserInfo?.bulkRecipientsCountLimitation
      ) {
        this.dialogService.openDialog({
          message: this.translate.instant('messages.bulkRecipientsLimitation', {
            count: this.currentUserInfo?.bulkRecipientsCountLimitation,
          }),
          title: this.translate.instant('common.error'),
          status: DialogStatus.error,
        });
        return;
      }
      this.scheduledMessageCalculationsApi({
        smsText: this.model.smsBody,
        senderNumberId: this.model.senderNumberId,
        bulkRecipients: this.model.bulkRecipients,
        voiceMessageId: this.model?.voiceMessageDetails?.voiceMessageId || null,
        socialMediaDetails: this.model?.socialMediaDetails || null,
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  unSubs() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  scheduledMessageCalculationsApi(
    model: GetScheduledMessageCalculationsByBulkQuery
  ) {
    this.unSubs();
    this.loading = true;
    this.calcLoading = true;

    this.currentService
      .scheduledMessagesGetScheduledMessageCalculationsByBulk({ body: model })
      .pipe(
        delay(500),
        tap((res: CustomResultOfGetWhiteMessageCalculationsDto) => {
          if (res.isSucceed) this.onConfirmation(res?.data);
        }),
        this.basePipeOperators(),
        finalize(() => (this.calcLoading = false))
      )
      .subscribe();
  }

  createScheduledMessageBulkApi(
    requestModel: CreateScheduledMessageBulkCommand
  ) {
    this.unSubs();
    this.loading = true;
    this.sendLoading = true;

    this.currentService
      .scheduledMessagesCreateScheduledMessageBulkPost({
        body: requestModel,
      })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.sendLoading = false))
      )
      .subscribe((result) => {
        this.apiNotificationService.notifyMessage({
          result,
          mode: 'matDialog',
        });
        if (result?.isSucceed) {
          this.onSuccess();
          this.refreshPage();
        }
      });
  }

  onConfirmation(model: GetWhiteMessageCalculationsDto) {
    this.unSubs();
    try {
      let data = {
        model: model,
        smsCount: this.smsCount,
        privateNumber: this.privateNumber,
      };

      const modalResult = this.dialogService.openModal({
        component: SendSmsConfirmationComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((confirm) => {
          if (confirm) this.createScheduledMessageBulkApi(this.model);
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  onSaveToCampaign(event) {
    
    try {
      this.setBulkRecipients();
      this.model.saveToCampaign = event;

      const modalResult = this.dialogService.openModal({
        component: CampaignsFormComponent,
        data: { model: this.model, sendType: SendTypes.Bulk },
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          this.model.saveToCampaign = res?.isSaveAfterSend;
          if (res?.campaignId) this.model.campaignId = res?.campaignId;
          if (res?.title) this.model.title = res?.title;
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  openPage(tabName: string) {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab(tabName);
      this.dynamicTabService.setActiveTab(tabName);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  onShowHelpPage() {
    this.isVisibleHelpPage = true;
  }

  onHideHelpPage() {
    this.isVisibleHelpPage = false;
  }

  getPrivateNumber(number: string) {
    this.privateNumber = number;
  }

  refreshPage() {
    this.tabsDynamicComponent.refreshTab(this.tabName);
  }
}
